import React from 'react';
import { useTranslation } from 'react-i18next';
import { CashFlowPage } from './CashFlowPage';
import { PerformancePage } from './PerformancePage';
import { TrafficPage } from './TrafficPage';
import { ForecastPage } from './ForecastPage';
import { PageType } from '../modals/PageTypeSelectionModal';
import { BoardGrid } from '../BoardGrid';
import { isForecastingEnabled, isTrafficAttributionEnabled } from '@/lib/features';

interface PageRendererProps {
    pageType?: PageType | string;
    isEditMode: boolean;
    // Fallback props for BoardGrid
    board: any;
    items: any[];
    isChartLoading: boolean;
    isChartError: boolean;
    onChartErrorClick: () => void;
    onLayoutChange: () => void;
    onDragStop: any;
    onResizeStop: any;
    onDrop: any;
    onRemoveItem: any;
    droppingItem: any;
    onDrag: any;
    onAddCharts: (location?: { x: number, y: number }) => void;
    onToggleEditMode?: (enabled: boolean) => void;
}

/**
 * Registry of custom page types that have their own specialized views.
 * Pages added here will not use the standard BoardGrid.
 */
export const CUSTOM_PAGES: Record<string, React.FC<any>> = {
    'Cash Flow': CashFlowPage,
    'Performance': PerformancePage,
    'Traffic': TrafficPage,
    'Forecast': ForecastPage,
    // Future custom pages can be easily added here:
};

/**
 * Returns true if the page type is a tailored full-width page.
 */
export const isCustomPage = (pageType?: string): boolean => {
    return !!(pageType && CUSTOM_PAGES[pageType]);
};

export const isPageTypeDisabled = (pageType?: string): boolean => {
    if (pageType === 'Forecast' && !isForecastingEnabled()) return true;
    if (pageType === 'Traffic' && !isTrafficAttributionEnabled()) return true;
    return false;
};

export const DisabledPagePlaceholder: React.FC<{ pageType?: string }> = ({ pageType }) => {
    const { t } = useTranslation('common');
    return (
        <div className="h-full w-full flex flex-col items-center justify-center p-8 text-center select-none bg-neutral-50/50 dark:bg-neutral-900/30">
            <div className="w-12 h-12 rounded-2xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center mb-4 text-neutral-400">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
            </div>
            <h3 className="text-base font-semibold text-neutral-700 dark:text-neutral-200 mb-1">
                {pageType ? t(`templates.pageTypes.${pageType.toLowerCase().replace(' ', '')}`, pageType) : ''}
            </h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 max-w-sm">
                {t('charts.currentlyUnavailable', 'This chart is currently unavailable')}
            </p>
        </div>
    );
};

export const PageRenderer: React.FC<PageRendererProps> = (props) => {
    const { pageType, isEditMode, onToggleEditMode } = props;

    if (isPageTypeDisabled(pageType)) {
        return (
            <div className="h-full w-full flex flex-col overflow-hidden">
                <DisabledPagePlaceholder pageType={pageType} />
            </div>
        );
    }

    const CustomComponent = CUSTOM_PAGES[pageType as string];

    if (CustomComponent) {
        return (
            <div className="h-full w-full flex flex-col overflow-hidden">
                <CustomComponent isEditMode={pageType === 'Performance' ? false : isEditMode} />
            </div>
        );
    }

    // Default to BoardGrid for standard pages
    return (
        <div className="p-2 lg:p-4 min-h-full">
            <BoardGrid
                board={props.board}
                items={props.items}
                isEditMode={props.isEditMode}
                isChartLoading={props.isChartLoading}
                isChartError={props.isChartError}
                onChartErrorClick={props.onChartErrorClick}
                onLayoutChange={props.onLayoutChange}
                onDragStop={props.onDragStop}
                onResizeStop={props.onResizeStop}
                onDrop={props.onDrop}
                onRemoveItem={props.onRemoveItem}
                droppingItem={props.droppingItem}
                onDrag={props.onDrag}
                onAddCharts={props.onAddCharts}
                onToggleEditMode={onToggleEditMode}
            />
        </div>
    );
};
